// DataSource implemented over dicomweb-client. This is the ONLY module that
// imports dicomweb-client.

import { api } from 'dicomweb-client';
import type { DataSource, StudyQuery } from '../DataSource';
import type { DicomJson } from '../dicomTags';
import { getConfig } from '../../config/runtimeConfig';

// Tags we want QIDO to return so the worklist/case list can render without an
// extra metadata round-trip.
const STUDY_INCLUDE = [
  'PatientName',
  'PatientID',
  'StudyDate',
  'StudyTime',
  'AccessionNumber',
  'StudyDescription',
  'ModalitiesInStudy',
  'NumberOfStudyRelatedSeries',
  'NumberOfStudyRelatedInstances',
].join(',');

const SERIES_INCLUDE = [
  'SeriesNumber',
  'Modality',
  'SeriesDescription',
  'NumberOfSeriesRelatedInstances',
].join(',');

export class DicomWebDataSource implements DataSource {
  private client: InstanceType<typeof api.DICOMwebClient>;
  private readonly root: string;

  constructor(dicomWebRoot: string) {
    this.root = dicomWebRoot.replace(/\/$/, '');
    // withCredentials so the session cookie rides along.
    //
    // Redundant in a deployed shape, where the app and the API share an origin
    // and the browser attaches the cookie without being asked. It matters in
    // DEVELOPMENT, where the viewer is served from :3003 and the server from
    // :8042 — a cross-origin XHR sends no cookie unless it is set, so signing in
    // succeeds and then every study query 401s. An empty worklist behind a
    // successful login is a confusing way to find that out.
    //
    // Safe on a same-origin request, and cross-origin it still requires the
    // server to name this exact origin in CORS_ALLOWED_ORIGINS and answer with
    // Allow-Credentials — a wildcard will not do, which the browser enforces.
    this.client = new api.DICOMwebClient({
      url: dicomWebRoot,
      // Send the session cookie. Via a request hook because the client exposes
      // no withCredentials option — the hook receives the XHR itself and must
      // return it.
      //
      // ONLY WHERE THERE IS A SESSION TO SEND. Setting it unconditionally broke
      // the default development stack, and browser-blind tests could not see
      // it: `Access-Control-Allow-Origin: *` and credentials are mutually
      // exclusive, and CORS_ALLOWED_ORIGINS defaults to `*`, so every request
      // died as an opaque net::ERR_FAILED — an empty worklist and a console
      // that does not say why. With no auth configured there is no cookie worth
      // sending, so asking for one buys nothing and costs that.
      //
      // BOTH parameters are declared even though only the first is used. The
      // client validates hooks with `requestHook.length === 2`, so a
      // single-parameter hook is rejected and silently skipped — it warns to
      // the console and carries on unauthenticated, which looks exactly like
      // the hook having no effect.
      requestHooks: [
        (request: XMLHttpRequest, _metadata: unknown) => {
          request.withCredentials = getConfig().auth !== null;
          return request;
        },
      ],
    } as ConstructorParameters<typeof api.DICOMwebClient>[0]);
  }

  rawClient(): unknown {
    return this.client;
  }

  studyThumbnailUrl(studyInstanceUID: string): string {
    return `${this.root}/studies/${studyInstanceUID}/thumbnail`;
  }

  seriesThumbnailUrl(studyInstanceUID: string, seriesInstanceUID: string): string {
    return `${this.root}/studies/${studyInstanceUID}/series/${seriesInstanceUID}/thumbnail`;
  }

  async searchStudies(query: StudyQuery = {}): Promise<DicomJson[]> {
    const queryParams: Record<string, string | number> = {
      includefield: STUDY_INCLUDE,
      limit: query.limit ?? 200,
    };
    if (query.offset) queryParams.offset = query.offset;
    if (query.patientId) queryParams.PatientID = query.patientId;
    if (query.patientName) queryParams.PatientName = `*${query.patientName}*`;
    if (query.accessionNumber) queryParams.AccessionNumber = query.accessionNumber;
    if (query.studyDate) queryParams.StudyDate = query.studyDate;
    if (query.modality) queryParams.ModalitiesInStudy = query.modality;
    if (query.workspace) queryParams.workspace = query.workspace;
    if (query.collection) queryParams.collection = query.collection;
    // Comma-joined: the server splits on commas and requires ALL of them, so
    // several tags narrow rather than widen.
    if (query.tags?.length) queryParams.tag = query.tags.join(',');
    const res = await this.client.searchForStudies({ queryParams });
    return res as DicomJson[];
  }

  async searchSeries(studyInstanceUID: string): Promise<DicomJson[]> {
    const res = await this.client.searchForSeries({
      studyInstanceUID,
      queryParams: { includefield: SERIES_INCLUDE },
    });
    return res as DicomJson[];
  }

  async searchInstances(
    studyInstanceUID: string,
    seriesInstanceUID: string,
  ): Promise<DicomJson[]> {
    const res = await this.client.searchForInstances({ studyInstanceUID, seriesInstanceUID });
    return res as DicomJson[];
  }

  async retrieveSeriesMetadata(
    studyInstanceUID: string,
    seriesInstanceUID: string,
  ): Promise<DicomJson[]> {
    const res = await this.client.retrieveSeriesMetadata({ studyInstanceUID, seriesInstanceUID });
    return res as DicomJson[];
  }

  async retrieveInstanceMetadata(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
  ): Promise<DicomJson[]> {
    const res = await this.client.retrieveInstanceMetadata({
      studyInstanceUID,
      seriesInstanceUID,
      sopInstanceUID,
    });
    return res as DicomJson[];
  }

  async retrieveFrame(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
    frameNumber: number,
  ): Promise<ArrayBuffer | null> {
    const frames = await this.client.retrieveInstanceFrames({
      studyInstanceUID,
      seriesInstanceUID,
      sopInstanceUID,
      frameNumbers: [frameNumber],
      mediaTypes: [{ mediaType: 'image/jpeg' }],
    });
    return frames[0] ?? null;
  }
}
